
import React, { useState, useMemo } from 'react';
import { 
    Table, Input, Row, Col, Button, Card, CardBody, CardHeader, Label, 
    Modal, ModalHeader, ModalBody, ModalFooter 
} from 'reactstrap';
import { 
    useReactTable, getCoreRowModel, getFilteredRowModel, 
    getPaginationRowModel, getSortedRowModel, flexRender 
} from '@tanstack/react-table';
import { Link } from "react-router-dom";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
};

const TabelaEspacos = ({ data = [], onDelete, feiraId, isFeiraContext = false}) => {
    const [globalFilter, setGlobalFilter] = useState('');
    const [pavilhaoSelecionado, setPavilhaoSelecionado] = useState('');
    const [minArea, setMinArea] = useState('');
    const [maxArea, setMaxArea] = useState('');
    const [minValor, setMinValor] = useState('');
    const [maxValor, setMaxValor] = useState('');

    const [deleteModal, setDeleteModal] = useState(false);
    const [idToDelete, setIdToDelete] = useState(null);
    const authUser = useMemo(() => JSON.parse(sessionStorage.getItem("authUser")) || {}, []);
    const userData = authUser.user || authUser;
    const liberaExclusao = ['JOTAPIX', 'EVOMAP'].includes(userData.tipo);
    
    const toggleDeleteModal = () => setDeleteModal(!deleteModal);

    const confirmDelete = (id) => {
        setIdToDelete(id);
        toggleDeleteModal();
    };

    const handleDelete = () => {
        if (idToDelete) {
            onDelete(idToDelete);
            toggleDeleteModal();
        }
    };

    const dadosProcessados = useMemo(() => {
        return data.filter(item => {
            // 1. Filtro de Pavilhão
            const itemPavilhao = item.pavilhao && item.pavilhao.length > 0 ? item.pavilhao[0].nome : '';
            if (pavilhaoSelecionado && itemPavilhao !== pavilhaoSelecionado) return false;

            // 2. Filtro de Área (Faixa)
            const area = parseFloat(item.area || 0);
            if (minArea && area < parseFloat(minArea)) return false;
            if (maxArea && area > parseFloat(maxArea)) return false;

            // 3. Filtro de Valor (Faixa)
            const valor = parseFloat(item.valor_total_geral || 0);
            if (minValor && valor < parseFloat(minValor)) return false;
            if (maxValor && valor > parseFloat(maxValor)) return false;

            return true;
        });
    }, [data, pavilhaoSelecionado, minArea, maxArea, minValor, maxValor]);

    const pavilhoesOptions = useMemo(() => {
        const setPavilhoes = new Set();
        data.forEach(item => {
            if (item.pavilhao && item.pavilhao.length > 0) {
                setPavilhoes.add(item.pavilhao[0].nome);
            }
        });
        return [...setPavilhoes].sort();
    }, [data]);

    const columns = useMemo(() => [
        {
            header: "Espaço",
            accessorKey: "nome",
            sortingFn: "alphanumeric",
        },
        {
            header: "Pavilhão",
            accessorFn: (row) => row.pavilhao?.[0]?.nome || '--',
        },
        {
            header: "Expositor",
            accessorFn: (row) => row.expositor?.[0]?.nome || 'Livre',
        },
        {
            header: "Área (m²)",
            accessorKey: "area",
            cell: info => {
                const val = parseFloat(info.getValue());
                return isNaN(val) ? '0.00' : val.toFixed(2);
            }
        },
        {
            header: "Valor Total",
            accessorKey: "valor_total_geral",
            cell: info => formatCurrency(info.getValue())
        },
        {
            header: "Ações",
            id: "acoes",
            enableSorting: false,
            cell: (cell) => {
                const espaco = cell.row.original;
                
                return (
                    <div className="d-flex gap-2">
                        <Link 
                            to={`/espaco/${espaco.id}/${feiraId}`} 
                            state={{ fromFeira: isFeiraContext }}
                            className="btn btn-sm btn-soft-primary"
                            title="Editar"
                        >
                            <i className="ri-pencil-line"></i>
                        </Link>
                        {
                            liberaExclusao && (
                                <Button 
                                    color="soft-danger" 
                                    size="sm" 
                                    onClick={() => confirmDelete(espaco.id)}
                                    title="Excluir"
                                >
                                    <i className="ri-delete-bin-line"></i>
                                </Button>)
                        }
                    </div>
                );
            }
        }
    ], [feiraId, isFeiraContext ]);

    const table = useReactTable({
        data: dadosProcessados,
        columns,
        state: { globalFilter },
        initialState: {
            sorting: [
                {
                    id: 'nome', // ID da coluna que deve bater com o accessorKey
                    desc: false, // false para A-Z, true para Z-A
                },
            ],
        },
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const getExportData = () => {
        const rows = table.getFilteredRowModel().rows.map(r => r.original);
        return rows.map(item => ({
            Espaco: item.nome,
            Pavilhao: item.pavilhao?.[0]?.nome || '--',
            Expositor: item.expositor?.[0]?.nome || 'Livre',
            Area: parseFloat(item.area || 0),
            Valor: parseFloat(item.valor_total_geral || 0)
        }));
    };

    const exportarExcel = () => {
        const dadosExport = getExportData();
        if(dadosExport.length === 0) return alert("Sem dados para exportar");

        const totalArea = dadosExport.reduce((acc, curr) => acc + curr.Area, 0);
        const totalValor = dadosExport.reduce((acc, curr) => acc + curr.Valor, 0);

        dadosExport.push({ Espaco: 'TOTAL', Pavilhao: '', Expositor: '', Area: totalArea, Valor: totalValor });

        const worksheet = XLSX.utils.json_to_sheet(dadosExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Espacos");
        
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const dataBlob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(dataBlob, `Espacos_${new Date().getTime()}.xlsx`);
    };

    const exportarPDF = () => {
        const rows = getExportData();
        if(rows.length === 0) return alert("Sem dados para exportar");

        const doc = new jsPDF();
        doc.text("Relatório de Espaços", 14, 15);
        doc.setFontSize(10);
        doc.text(`Gerado em: ${new Date().toLocaleDateString()}`, 14, 22);

        const totalArea = rows.reduce((acc, curr) => acc + curr.Area, 0);
        const totalValor = rows.reduce((acc, curr) => acc + curr.Valor, 0);

        const tableBody = rows.map(row => [
            row.Espaco, row.Pavilhao, row.Expositor, row.Area.toFixed(2), formatCurrency(row.Valor)
        ]);
        tableBody.push(['TOTAL', '', '', totalArea.toFixed(2), formatCurrency(totalValor)]);

        autoTable(doc, {
            head: [['Espaço', 'Pavilhão', 'Expositor', 'Área (m²)', 'Valor Total']],
            body: tableBody,
            startY: 25,
            theme: 'grid',
            headStyles: { fillColor: [60, 60, 60] },
            footStyles: { fillColor: [240, 240, 240], textColor: [0,0,0], fontStyle: 'bold' }
        });
        doc.save(`Espacos_${new Date().getTime()}.pdf`);
    };

    return (
        <React.Fragment>
            <Row className="g-3">
                <Col md={3}>
                    <Label className="form-label">Pesquisa Global</Label>
                    <div className="search-box">
                        <Input 
                            type="text" 
                            className="form-control"
                            placeholder="Buscar por nome ou expositor..." 
                            value={globalFilter}
                            onChange={e => setGlobalFilter(e.target.value)}
                        />
                        <i className="ri-search-line search-icon"></i>
                    </div>
                </Col>
                <Col md={3}>
                    <Label className="form-label">Pavilhão</Label>
                    <Input type="select" className="form-select" value={pavilhaoSelecionado} onChange={e => setPavilhaoSelecionado(e.target.value)}>
                        <option value="">Todos</option>
                        {pavilhoesOptions.map((p, idx) => (
                            <option key={idx} value={p}>{p}</option>
                        ))}
                    </Input>
                </Col>
                <Col md={3}>
                    <Label className="form-label">Área (m²)</Label>
                    <div className="input-group">
                        <Input type="number" className="form-control" placeholder="Mín" value={minArea} onChange={e => setMinArea(e.target.value)} />
                        <span className="input-group-text">-</span>
                        <Input type="number" className="form-control" placeholder="Máx" value={maxArea} onChange={e => setMaxArea(e.target.value)} />
                    </div>
                </Col>
                <Col md={3}>
                    <Label className="form-label">Valor (R$)</Label>
                    <div className="input-group">
                        <Input type="number" className="form-control" placeholder="Mín" value={minValor} onChange={e => setMinValor(e.target.value)} />
                        <span className="input-group-text">-</span>
                        <Input type="number" className="form-control" placeholder="Máx" value={maxValor} onChange={e => setMaxValor(e.target.value)} />
                    </div>
                </Col>
            </Row>

            <Card className="mt-3">
                <CardHeader className="d-flex align-items-center justify-content-between border-bottom-0">
                    <h5 className="card-title mb-0 flex-grow-1"></h5>
                    <div className="flex-shrink-0">
                        <Link to={`/espaco/novo/${feiraId}`} state={{ fromFeira: isFeiraContext }} className="btn btn-success btn-sm me-2">
                            <i className="ri-add-line align-bottom me-1"></i> Novo Espaço
                        </Link>
                        <div className="d-inline-flex gap-1">
                            <Button color="success" size="sm" onClick={exportarExcel} title="Exportar Excel">
                                <i className="ri-file-excel-2-line"></i>
                            </Button>
                            <Button color="danger" size="sm" onClick={exportarPDF} title="Exportar PDF">
                                <i className="ri-file-pdf-line"></i>
                            </Button>
                        </div>
                    </div>
                </CardHeader>
                
                <CardBody>
                    <div className="table-responsive table-card">
                        <Table className="align-middle table-nowrap table-striped table-hover mb-0">
                            <thead className="table-light">
                                {table.getHeaderGroups().map(headerGroup => (
                                    <tr key={headerGroup.id}>
                                        {headerGroup.headers.map(header => (
                                            <th key={header.id} onClick={header.column.getToggleSortingHandler()} style={{ cursor: 'pointer' }}>
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                {{ asc: ' ↑', desc: ' ↓' }[header.column.getIsSorted()] ?? null}
                                            </th>
                                        ))}
                                    </tr>
                                ))}
                            </thead>
                            <tbody>
                                {table.getRowModel().rows.length > 0 ? (
                                    table.getRowModel().rows.map(row => (
                                        <tr key={row.id}>
                                            {row.getVisibleCells().map(cell => (
                                                <td key={cell.id}>
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </td>
                                            ))}
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={columns.length} className="text-center py-4">
                                            <div className="text-muted">Nenhum registro encontrado.</div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>

                    <div className="row align-items-center mt-3">
                        <div className="col-sm">
                            <div className="text-muted text-sm">
                                Mostrando <span className="fw-semibold">{table.getRowModel().rows.length}</span> resultados de {dadosProcessados.length}
                            </div>
                        </div>
                        <div className="col-sm-auto">
                            <div className="d-flex gap-1">
                                <Button color="primary" size="sm" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
                                    Anterior
                                </Button>
                                <Button color="primary" size="sm" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
                                    Próximo
                                </Button>
                            </div>
                        </div>
                    </div>
                </CardBody>
            </Card>

            <Modal isOpen={deleteModal} toggle={toggleDeleteModal} centered>
                <ModalHeader toggle={toggleDeleteModal}>Confirmar Exclusão</ModalHeader>
                <ModalBody>
                    <div className="mt-2 text-center">
                        <i className="ri-error-warning-line display-5 text-danger mb-2"></i>
                        <h4>Você tem certeza?</h4>
                        <p className="text-muted mx-4 mb-0">
                            Deseja realmente excluir este espaço? Esta ação não poderá ser desfeita.
                        </p>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={toggleDeleteModal}>Cancelar</Button>
                    <Button color="danger" onClick={handleDelete}>Sim, excluir</Button>
                </ModalFooter>
            </Modal>
        </React.Fragment>
    );
};

export default TabelaEspacos;